import { useMemo } from "react";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { API_BASE_URL } from "@/lib/apiBase";

/**
 * Social write API bases. When inside OrgSocialActorProvider, all writes must
 * use org-scoped endpoints — never personal /social/* write routes.
 */
export function useSocialWriteApis() {
  const actor = useOrgSocialActor();
  const actAsOrgId = actor?.orgId || null;
  const canPublish = Boolean(actor?.canPublish);
  const canManageSettings = Boolean(actor?.canManageSettings);
  const isActAsOrg = Boolean(actAsOrgId);

  return useMemo(() => {
    const orgBase = actAsOrgId
      ? `${API_BASE_URL}/org/${encodeURIComponent(actAsOrgId)}/social`
      : null;

    return {
      isActAsOrg,
      actAsOrgId,
      canPublish: isActAsOrg ? canPublish : true,
      canManageSettings: isActAsOrg ? canManageSettings : false,
      displayName: actor?.displayName || null,
      handle: actor?.handle || null,
      orgProfile: actor?.orgProfile || null,
      /** POST/DELETE .../posts/:id/interactions */
      postsBase: orgBase
        ? `${orgBase}/posts`
        : `${API_BASE_URL}/social/posts`,
      /** POST/DELETE .../posts/:id/repost */
      repostUrl: (postId) =>
        orgBase
          ? `${orgBase}/posts/${encodeURIComponent(String(postId))}/repost`
          : `${API_BASE_URL}/social/posts/${encodeURIComponent(String(postId))}/repost`,
      interactionsUrl: (postId) =>
        `${
          orgBase
            ? `${orgBase}/posts`
            : `${API_BASE_URL}/social/posts`
        }/${encodeURIComponent(String(postId))}/interactions`,
      followUserUrl: (handle) =>
        orgBase
          ? `${orgBase}/follow/users/${encodeURIComponent(handle)}`
          : `${API_BASE_URL}/social/users/${encodeURIComponent(handle)}/follow`,
      followOrgUrl: (handle) =>
        orgBase
          ? `${orgBase}/follow/orgs/${encodeURIComponent(handle)}`
          : `${API_BASE_URL}/social/orgs/${encodeURIComponent(handle)}/follow`,
      createPostUrl: orgBase
        ? `${orgBase}/posts`
        : `${API_BASE_URL}/social/posts`,
      /** Hide personal Save / Send under act-as (no org saved-posts / share UX yet). */
      allowSave: !isActAsOrg,
      allowShareToDm: !isActAsOrg,
      allowAmplify: isActAsOrg ? canPublish : true,
      allowEngage: isActAsOrg ? canPublish : true,
    };
  }, [
    actAsOrgId,
    canPublish,
    canManageSettings,
    isActAsOrg,
    actor?.displayName,
    actor?.handle,
    actor?.orgProfile,
  ]);
}
